import { defineNuxtPlugin, useRouter, watch, ref, useRoute, navigateTo, useDevice, useUserInfo, useWebauthn, useMobileApp, useMobileAppPasskeys, until, useMarketingAutomationClient, useCoreConfig, useCookieConsent, useWindowScroll, useTracking } from '#imports'

/** Consent message names the app listens for; the same for every CMP. */
const APP_CONSENT_MESSAGE = {
    loaded: 'cookiebot_loaded',
    accepted: 'cookiebot_accepted',
    declined: 'cookiebot_declined',
} as const

interface MitIdBrowserResult {
    url?: string
    type?: 'success' | 'dismiss' | 'locked' | 'opened' | 'cancel' | 'access_denied' // Success, dismiss = App Tab browser was closed by user
    sessionId?: string
}

interface EidBrowserPending {
    v?: number
    provider?: string
    sessionId?: string
    minWaitMs?: number
}

export default defineNuxtPlugin({
    name: 'react-handles',
    parallel: true,
    setup(nuxtApp) {
        const router = useRouter()
        const route = useRoute()
        const { client } = useMarketingAutomationClient()
        const { isLoggedIn, data: userData } = useUserInfo()
        const { isIos } = useDevice()
        const config = useCoreConfig()
        const { trackEvent } = useTracking()
        const { isMobileApp, postToApp, pendingEidSessionId } = useMobileApp()
        const cookieConsent = useCookieConsent()

        if (isMobileApp) bridgeConsentToApp()

        nuxtApp.hooks.hookOnce('app:suspense:resolve', async () => {
            const { useGetPasskeys, passkeyLoginSucceeded, registrationState } = useWebauthn()
            const { hasPasskeyOnDevice, init: initMobilePasskeys, appPasskeyState, initiatePassKeyLogin } = useMobileAppPasskeys()

            if (!isMobileApp) return

            // Must install window.passKeyFunctions before the "loaded" message below — the app calls into the bridge on receipt.
            initMobilePasskeys()

            // Single owner for passkey auto-login: this plugin runs exactly once, so there's
            // one watcher. (Previously the watcher lived in usePasskeyLoginFlow, a shared
            // composable instantiated by several components, which spun up multiple watchers
            // and duplicated the authentication-options request.)
            if (config.webauthn?.enabled) {
                watch(appPasskeyState, async (state) => {
                    if (state !== 'CREATED' || isLoggedIn.value) return

                    await initiatePassKeyLogin()

                    // On success the page is already reloading; only fall back to the login
                    // screen if the attempt errored (a plain dismiss leaves the user where they are).
                    if (!passkeyLoginSucceeded.value && registrationState.value.error) {
                        await navigateTo({ query: { auth: 'login', passkey_error: '1' } })
                    }
                }, { immediate: true })
            }

            const { data: passkeys, isLoading } = useGetPasskeys({
                enabled: isLoggedIn
            })

            // Scroll
            const { y } = useWindowScroll(window)
            const scrollIsAtTop = ref<boolean>(true)
            // Set initial scrollIsTop value
            if (y.value > 0) {
                scrollIsAtTop.value = false
                emitScrollTop(scrollIsAtTop.value)
            }

            // Scroll
            // event listening - ensure we'll only be able to "pull-down to refresh" in react native when the value is true
            watch(
                () => y.value === 0,
                (isAtTop, wasAtTop) => {
                    if (isAtTop !== wasAtTop) {
                        scrollIsAtTop.value = isAtTop
                        emitScrollTop(scrollIsAtTop.value)
                    }
                }
            )

            // On a hard load into playgame this runs after Playgame has mounted, so a fixed
            // 'site' would overwrite the 'playgame' env it already sent and the app would
            // paint the default layout's chrome around the game.
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'env', env: route.meta.isPlaygame ? 'playgame' : 'site' }))

            // Wait for passkeys to have loaded if user is logged in
            if (isLoggedIn.value) {
                await until(() => isLoading.value).toBe(false)
            }

            // Emit loaded event to react-container
            // Add user passkey status
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'loaded', isLoggedIn: isLoggedIn.value, hasPasskey: isLoggedIn.value ? hasPasskeyOnDevice(passkeys?.value?.data || []) : false }))
        })

        // Unparseable URLs count as MitID so tracking and routing still run.
        function hasMobilePayParam(url: string) {
            try {
                return new URL(url).searchParams.get('mobilepay') === 'true'
            }
            catch {
                return false
            }
        }

        function emitScrollTop(val: boolean) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'scroll', top: val }))
        }

        /** Keeps the app informed of the visitor's consent decision, whichever CMP the site runs. */
        function bridgeConsentToApp() {
            const { consent, isConsentReady, isDeclined, whenLoaded, hide } = cookieConsent

            // iOS: keep the banner hidden until the visitor has answered the native tracking prompt.
            // The app reports the outcome through acceptTracking() / declineTracking().
            if (isIos) {
                whenLoaded().then((loaded) => {
                    if (!loaded) return
                    hide()
                    if (isDeclined.value) return
                    postToApp({ type: APP_CONSENT_MESSAGE.loaded })
                })
            }

            watch([isConsentReady, consent], ([ready, { marketing, statistics, preferences }]) => {
                if (!ready) return
                postToApp({
                    type: isDeclined.value ? APP_CONSENT_MESSAGE.declined : APP_CONSENT_MESSAGE.accepted,
                    marketing,
                    statistics,
                    preferences,
                })
            }, { immediate: true })
        }

        // Handles both MitID and MobilePay results; handleMitIdBrowserResult stays as an alias
        // for old app builds.
        async function handleEidBrowserResult(result?: MitIdBrowserResult) {
            if (!result) return

            // Only a result for the pending session (or one that carries none) ends the wait.
            if (!result.sessionId || result.sessionId === pendingEidSessionId.value) {
                pendingEidSessionId.value = undefined
            }

            const updateUrl = async (inputUrl: string | null, params = {}) => {
                // If no URL is provided, fall back to the current page’s URL
                const finalUrlString = (inputUrl ?? window.location.href).replace('spilnu://', `${window.location.origin}/`)

                const newUrl = new URL(finalUrlString)

                // Remove the '/eid/<provider>' deep-link path if present
                // For instance, if pathname was '/eid/mitid/konto/eid', it becomes '/konto/eid'
                newUrl.pathname = newUrl.pathname.replace(/\/eid\/(mitid|mobilepay)/, '')

                // Clear and re-set the search parameters. No encodeURIComponent: the values read
                // out of the result URL are already decoded and searchParams encodes on
                // serialization, so encoding here reached the backend double-encoded.
                newUrl.search = ''
                for (const [key, value] of Object.entries(params)) {
                    newUrl.searchParams.set(key, value as string)
                }

                // Redirect to the updated URL
                navigateTo(newUrl.toString(), { external: true })
            }

            const isNotSuccess = 'type' in result && result.type !== 'success'
            const isDismissedOrCancelled = result.type && ['dismiss', 'cancel', 'access_denied'].includes(result.type)
            const isAbortedOrAccessDenied = result.url && ['aborted', 'access_denied'].some(c => result.url!.includes(c))

            // Dismissals carry no URL, so fall back to the mobilepay_signup cookie, which
            // is set for the duration of a MobilePay flow (see useLoginWithMobilePay).
            const isMobilePayResult = result.url
                ? result.url.includes('eid/mobilepay') || hasMobilePayParam(result.url)
                : document.cookie.includes('mobilepay_signup=')

            trackEvent({
                event_name: `app_mitid_browser_result`,
                event_type: result?.type || 'unknown',
                meta_data: [{ data: JSON.stringify(result), provider: isMobilePayResult ? 'mobilepay' : 'mitid' }],
                log_entry: true
            })

            if ((isNotSuccess && isDismissedOrCancelled) || isAbortedOrAccessDenied) {
                // mobilepay=true routes to /eid/callback, where the exchange reports the
                // error and reopens the auth dialog.
                await updateUrl(null, isMobilePayResult
                    ? {
                            error: 'dismissed_or_cancelled',
                            error_description: 'User dismissed or otherwise closed the MobilePay window',
                            mobilepay: 'true',
                        }
                    : {
                            error: 'dismissed_or_cancelled',
                            error_description: 'User dismissed or otherwise closed the MitId window',
                            mitid: 'true',
                            mitid_provider: 'unknown',
                        })

                return
            }

            if (!result.url) return
            // Extract the existing query parameters from the provided URL
            const url = new URL(result.url)
            const searchParams = Object.fromEntries(url.searchParams.entries())

            await updateUrl(result.url, searchParams)
        }

        // Android MobilePay: the user is back in the app without a callback yet, and the app
        // still owes exactly one result for this session. minWaitMs is only a lower bound on
        // when it lands, so it drives nothing here.
        function handleEidBrowserPending(pending?: EidBrowserPending) {
            if (pending?.v !== 1 || !pending.sessionId) return

            pendingEidSessionId.value = pending.sessionId
        }

        // Add website handles
        window.websiteFunctions = {
            goBack: () => {
                if (route.meta.isPlaygame) {
                    return
                }

                router.back()
            },
            eidFunctions: {
                handleEidBrowserResult,
                handleMitIdBrowserResult: handleEidBrowserResult,
                handleEidBrowserPending,
            },
            declineTracking: cookieConsent.declineAll,
            acceptTracking: () => {
                // Native tracking permission granted: let the visitor answer the banner if they have not.
                if (!cookieConsent.isConsentReady.value) cookieConsent.show()
            },
            async receiveAppleAdsToken(token: string, deviceId?: string) {
                try {
                    await client.POST('/api/v1/public/apple/ads/attribution/token', {
                        body: {
                            apple_ads_token: token,
                            ...(deviceId && { device_id: deviceId }),
                            ...(isLoggedIn.value && { player_id: userData?.value?.playerId })
                        }
                    })
                }
                catch (error) {
                    trackEvent({
                        event_name: 'apple_ads_attribution_token_error',
                        event_type: 'get',
                        meta_data: [{ data: JSON.stringify({ error }) }]
                    })
                }
            },
            async receiveNotificationToken(token: string, state: 'active' | 'disabled') {
                try {
                    await client.POST('/api/v1/public/apptoken/', {
                        body: {
                            app_token: token,
                            ...(isLoggedIn.value && { player_id: userData?.value?.playerId }),
                            os: isIos ? 'iOS' : 'Android',
                            state,
                            brand: config.site.brand
                        }
                    })
                }
                catch (error) {
                    trackEvent({
                        event_name: 'app_notification_token_error',
                        event_type: 'update',
                        meta_data: [{ data: token, error }]
                    })
                }
            },
            trackNotificationOpen(payload: Record<string, unknown>) {
                // Called by the native app when a push notification is opened.
                // User id is attached by useTracking, so we don't pass player_id here.
                trackEvent({
                    event_name: 'app_notification_open',
                    event_type: 'open',
                    meta_data: [{ data: JSON.stringify(payload) }]
                })
            }
        }
    },
})
